import type { InputEvent, InputProvider } from './InputProvider';

export interface KeyMappingEntry {
  keyCode: string;
  scanCode: number;
}

export class KeyboardInput implements InputProvider {
  readonly name = 'keyboard';
  onInput: ((event: InputEvent) => void) | null = null;

  private _element: HTMLElement | null = null;
  private _suspended = false;
  private _keyMapping: Map<string, KeyMappingEntry>;
  private _pressedModifiers: Set<string> = new Set();

  private _handleKeyDown = (e: KeyboardEvent): void => {
    if (this._suspended) return;
    if (e.repeat) return;

    const entry = this._keyMapping.get(e.key);
    if (entry) {
      this._fire(entry.keyCode, entry.scanCode, true);
    }

    this._trackModifierKeys(e, true);
    e.preventDefault();
  };

  private _handleKeyUp = (e: KeyboardEvent): void => {
    if (this._suspended) return;

    const entry = this._keyMapping.get(e.key);
    if (entry) {
      this._fire(entry.keyCode, entry.scanCode, false);
    }

    this._trackModifierKeys(e, false);
    e.preventDefault();
  };

  constructor(keyMapping: Map<string, KeyMappingEntry>) {
    this._keyMapping = keyMapping;
  }

  attach(element: HTMLElement): void {
    if (this._element) {
      this.detach();
    }
    this._element = element;
    element.addEventListener('keydown', this._handleKeyDown);
    element.addEventListener('keyup', this._handleKeyUp);
  }

  detach(): void {
    if (this._element) {
      this._element.removeEventListener('keydown', this._handleKeyDown);
      this._element.removeEventListener('keyup', this._handleKeyUp);
      this._element = null;
    }
  }

  suspend(): void {
    this._suspended = true;
  }

  resume(): void {
    this._suspended = false;
  }

  private _trackModifierKeys(e: KeyboardEvent, pressed: boolean): void {
    const mods: Array<{ key: string; flag: boolean }> = [
      { key: 'Shift', flag: e.shiftKey },
      { key: 'Alt', flag: e.altKey },
      { key: 'Control', flag: e.ctrlKey },
    ];

    for (const mod of mods) {
      const modEntry = this._keyMapping.get(mod.key);
      if (!modEntry) continue;

      const isModPressed = this._pressedModifiers.has(mod.key);
      if (pressed && mod.flag && !isModPressed) {
        this._pressedModifiers.add(mod.key);
        if (e.key !== mod.key) {
          this._fire(modEntry.keyCode, modEntry.scanCode, true);
        }
      } else if (!pressed && !mod.flag && isModPressed) {
        this._pressedModifiers.delete(mod.key);
        if (e.key !== mod.key) {
          this._fire(modEntry.keyCode, modEntry.scanCode, false);
        }
      }
    }
  }

  private _fire(keyCode: string, scanCode: number, pressed: boolean): void {
    if (this.onInput) {
      this.onInput({ keyCode, scanCode, pressed, timestamp: Date.now() });
    }
  }
}
